<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import axios from 'axios'

export interface Option {
  id: number
  label: string
  value?: string
}

const props = defineProps<{
  modelValue: number[]
  placeholder?: string
  class?: string
  id?: string
  loadOptionsUrl: string
  selectedOptions?: Option[] // For displaying selected items
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number[]]
}>()

const isOpen = ref(false)
const searchQuery = ref('')
const containerRef = ref<HTMLElement>()
const dropdownContentRef = ref<HTMLElement>()
const asyncOptions = ref<Option[]>([])
const isLoading = ref(false)
const hasMore = ref(true)
const currentPage = ref(1)
const allLoadedOptions = ref<Option[]>([]) // Keep track of all loaded options
const selectedOptionsCache = ref<Map<number, Option>>(new Map()) // Cache selected options for display
let searchTimeout: ReturnType<typeof setTimeout> | null = null

const pageSize = 100

// Check if option is selected
const isSelected = (optionId: number) => {
  return props.modelValue.includes(optionId)
}

// Toggle option selection
const toggleOption = (optionId: number) => {
  const newValue = [...props.modelValue]
  const index = newValue.indexOf(optionId)
  
  if (index > -1) {
    newValue.splice(index, 1)
    // Remove from cache
    selectedOptionsCache.value.delete(optionId)
  } else {
    newValue.push(optionId)
    // Add to cache - find the option from asyncOptions
    const option = asyncOptions.value.find(opt => opt.id === optionId)
    if (option) {
      selectedOptionsCache.value.set(optionId, option)
    }
  }
  
  emit('update:modelValue', newValue)
}

// Close dropdown manually
const closeDropdown = () => {
  isOpen.value = false
  searchQuery.value = ''
}

// Load options asynchronously
const loadAsyncOptions = async (search: string = '', page: number = 1, append: boolean = false) => {
  isLoading.value = true
  try {
    const params: any = { limit: pageSize, page: page }
    if (search) {
      params.q = search
    }
    
    const response = await axios.get(props.loadOptionsUrl, { params })
    const newOptions = response.data.map((item: any) => ({
      id: item.id,
      label: item.name || item.label,
      value: item.cas_number || item.value
    }))
    
    if (append) {
      // Append to existing options
      const existingIds = new Set(allLoadedOptions.value.map(opt => opt.id))
      const uniqueNewOptions = newOptions.filter((opt: Option) => !existingIds.has(opt.id))
      allLoadedOptions.value = [...allLoadedOptions.value, ...uniqueNewOptions]
    } else {
      // Replace options
      allLoadedOptions.value = newOptions
    }
    
    asyncOptions.value = allLoadedOptions.value
    
    // Update cache with any selected options that are in the loaded results
    newOptions.forEach(opt => {
      if (props.modelValue.includes(opt.id)) {
        selectedOptionsCache.value.set(opt.id, opt)
      }
    })
    
    hasMore.value = newOptions.length === pageSize
    currentPage.value = page
  } catch (error) {
    console.error('Failed to load options:', error)
    asyncOptions.value = []
    hasMore.value = false
  } finally {
    isLoading.value = false
  }
}

// Handle scroll to load more
const handleScroll = () => {
  if (!dropdownContentRef.value) return
  
  const element = dropdownContentRef.value
  const scrollTop = element.scrollTop
  const scrollHeight = element.scrollHeight
  const clientHeight = element.clientHeight
  
  // Load more when near bottom (within 100px)
  if (scrollHeight - scrollTop - clientHeight < 100 && hasMore.value && !isLoading.value) {
    loadAsyncOptions(searchQuery.value, currentPage.value + 1, true)
  }
}

// Watch search query and debounce async loading
watch(searchQuery, (newQuery) => {
  // Only trigger if dropdown is open
  if (!isOpen.value) return
  
  // Clear existing timeout
  if (searchTimeout !== null) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  
  // Reset pagination when search changes
  currentPage.value = 1
  allLoadedOptions.value = []
  
  // Set new timeout for debounced search
  searchTimeout = setTimeout(() => {
    if (isOpen.value) { // Double check dropdown is still open
      const query = (newQuery || '').trim()
      loadAsyncOptions(query, 1, false)
    }
  }, 300)
})

// Load initial options when dropdown opens
watch(isOpen, (open) => {
  if (open) {
    // Always reset search and reload initial options when opening
    searchQuery.value = ''
    // Clear any pending search timeout
    if (searchTimeout !== null) {
      clearTimeout(searchTimeout)
      searchTimeout = null
    }
    // Reset pagination and options
    currentPage.value = 1
    allLoadedOptions.value = []
    asyncOptions.value = []
    // Load initial options
    loadAsyncOptions('', 1, false)
  } else {
    // Clear search when dropdown closes
    searchQuery.value = ''
    // Clear timeout if dropdown closes while searching
    if (searchTimeout !== null) {
      clearTimeout(searchTimeout)
      searchTimeout = null
    }
  }
})

// Get selected labels for display
const selectedLabels = computed(() => {
  if (props.modelValue.length === 0) return ''
  
  // Combine selectedOptions (from props) with cached selected items
  const selectedOptionsMap = new Map<number, Option>()
  
  // Add selectedOptions from props (for items loaded from server when editing)
  if (props.selectedOptions) {
    props.selectedOptions.forEach(opt => {
      if (props.modelValue.includes(opt.id)) {
        selectedOptionsMap.set(opt.id, opt)
        // Also add to cache
        selectedOptionsCache.value.set(opt.id, opt)
      }
    })
  }
  
  // Add cached options (for items selected during current session)
  selectedOptionsCache.value.forEach((opt, id) => {
    if (props.modelValue.includes(id)) {
      selectedOptionsMap.set(id, opt)
    }
  })
  
  // Remove items from cache that are no longer selected
  selectedOptionsCache.value.forEach((opt, id) => {
    if (!props.modelValue.includes(id)) {
      selectedOptionsCache.value.delete(id)
    }
  })
  
  const selected = Array.from(selectedOptionsMap.values())
    .map(opt => opt.value || opt.label)
  
  return selected.length > 2 
    ? `${selected.slice(0, 2).join(', ')} +${selected.length - 2} weitere`
    : selected.join(', ')
})
</script>

<template>
  <div ref="containerRef" class="relative w-full">
    <DropdownMenu :open="isOpen" @update:open="isOpen = $event">
      <DropdownMenuTrigger asChild>
        <Button
          :id="id"
          variant="outline"
          :class="cn('w-full justify-between', props.class)"
        >
          <span class="truncate">
            {{ selectedLabels || placeholder || 'Auswählen...' }}
          </span>
          <svg
            class="ml-2 h-4 w-4 shrink-0 opacity-50"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent 
        class="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64"
        @select.prevent
        @click.stop
      >
        <div 
          ref="dropdownContentRef"
          class="overflow-auto max-h-64"
          @scroll="handleScroll"
        >
          <DropdownMenuLabel>
            <input
              :value="searchQuery"
              @input="(e: any) => { searchQuery = e.target.value }"
              placeholder="Suchen..."
              class="h-8 flex w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              @click.stop
              @keydown.stop
              @mousedown.stop
            />
          </DropdownMenuLabel>

          <DropdownMenuGroup>
          <div v-if="isLoading && asyncOptions.length === 0" class="px-2 py-4 text-sm text-muted-foreground text-center">
            Lädt...
          </div>
          <template v-else>
            <DropdownMenuItem
              v-for="option in asyncOptions"
              :key="option.id"
              class="flex items-center gap-2 cursor-pointer"
              @select.prevent
              @click.stop="toggleOption(option.id)"
              @mousedown.stop
            >
              <input
                type="checkbox"
                :checked="isSelected(option.id)"
                class="accent-blue-500 pointer-events-none"
                @change.prevent
              />
              <div class="flex flex-col">
                <span class="font-medium">{{ option.value || option.label }}</span>
                <span v-if="option.value && option.label !== option.value" class="text-xs text-muted-foreground">{{ option.label }}</span>
              </div>
            </DropdownMenuItem>
          </template>
        </DropdownMenuGroup>

        <div
          v-if="!isLoading && asyncOptions.length === 0"
          class="px-2 py-4 text-sm text-muted-foreground text-center"
        >
          Keine Ergebnisse gefunden
        </div>

          <div v-if="isLoading && asyncOptions.length > 0" class="px-2 py-2 text-sm text-muted-foreground text-center">
            Lädt weitere...
          </div>
        </div>

        <div class="border-t p-2 flex justify-end">
          <Button
            type="button"
            size="sm"
            @click.stop="closeDropdown"
            @mousedown.stop
          >
            Speichern
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>
